import { useMemo } from "react";
import { useAppContext } from "../../../lib/contextLib";
import { useBudgetItems, useBudgetSummary } from '../../../lib/hooks/useBudgetQueries';
import { calculateTotals } from "../../../lib/calculateTotals";

interface BudgetTotalRow {
  index: number;
  typeTotal: string;
  amountWeek: number;
  amountFortnight: number;
  amountMonth: number;
  amountQuarter: number;
  amountYear: number;
  amountWeekPretty?: string;
  amountFortnightPretty?: string;
  amountMonthPretty?: string;
  amountQuarterPretty?: string;
  amountYearPretty?: string;
  [key: string]: unknown;
}

interface ChartDatum {
  name: string;
  value: number;
}

export interface TopItem {
  name: string;
  type: string;
  yearlyAmount: number;
  frequency: string;
}

export interface RuleComparison {
  label: string;
  actual: number;
  guideline: number;
  color: string;
}

export interface ExpenseBar {
  name: string;
  amount: number;
}

export interface BudgetOverviewData {
  isAuthenticated: boolean;
  isLoading: boolean;
  summaryLoading: boolean;
  summaryError: boolean;
  budgetTotals: BudgetTotalRow[];
  rechartsIncome: ChartDatum[];
  rechartsSavings: ChartDatum[];
  rechartsExpenses: ChartDatum[];
  dataOverall: ChartDatum[];
  // New widgets data
  savingsRate: number;
  topItems: TopItem[];
  expenseBarData: ExpenseBar[];
  ruleComparison: RuleComparison[];
}

const FREQ_LABELS: Record<string, string> = {
  W: 'Weekly', F: 'Fortnightly', M: 'Monthly', Q: 'Quarterly', Y: 'Yearly',
};

export function useBudgetOverview(): BudgetOverviewData {
  const { isAuthenticated, savingsTarget } = useAppContext();
  const { data: itemsData = [], isLoading: itemsLoading } = useBudgetItems(isAuthenticated);
  const { data: summary, isLoading: summaryLoading, isError: summaryError } = useBudgetSummary(isAuthenticated);

  const isLoading = itemsLoading || summaryLoading;

  const computed = useMemo(() => {
    const budgetTotals: BudgetTotalRow[] = [
      { index: 1, typeTotal: 'Income',   amountWeek: 0, amountFortnight: 0, amountMonth: 0, amountQuarter: 0, amountYear: 0 },
      { index: 2, typeTotal: 'Savings',  amountWeek: 0, amountFortnight: 0, amountMonth: 0, amountQuarter: 0, amountYear: 0 },
      { index: 3, typeTotal: 'Expenses', amountWeek: 0, amountFortnight: 0, amountMonth: 0, amountQuarter: 0, amountYear: 0 },
    ];

    const incomeSubtypes: { subtype: string; totalAmount: number; percentage?: string }[] = [];
    const savingSubtypes: { subtype: string; totalAmount: number; percentage?: string }[] = [];
    const expenseSubtypes: { subtype: string; totalAmount: number; percentage?: string }[] = [];
    const dataIncome: (string | number)[][] = [['Subtype', 'Amount']];
    const dataSavings: (string | number)[][] = [['Subtype', 'Amount']];
    const dataExpenses: (string | number)[][] = [['Subtype', 'Amount']];

    // Build per-item yearly amounts for top items list
    const allItems: TopItem[] = [];

    for (const budgetItem of itemsData) {
      let rowIndex: number;
      let accessArray: { subtype: string; totalAmount: number; percentage?: string }[];
      if      (budgetItem.itemType === 'income')  { rowIndex = 0; accessArray = incomeSubtypes; }
      else if (budgetItem.itemType === 'savings') { rowIndex = 1; accessArray = savingSubtypes; }
      else if (budgetItem.itemType === 'expense') { rowIndex = 2; accessArray = expenseSubtypes; }
      else continue;

      budgetTotals[rowIndex].amountWeek      += calculateTotals(budgetItem.amount, budgetItem.frequency, 'WEEK');
      budgetTotals[rowIndex].amountFortnight += calculateTotals(budgetItem.amount, budgetItem.frequency, 'FORTNIGHT');
      budgetTotals[rowIndex].amountMonth     += calculateTotals(budgetItem.amount, budgetItem.frequency, 'MONTH');
      budgetTotals[rowIndex].amountQuarter   += calculateTotals(budgetItem.amount, budgetItem.frequency, 'QUARTER');
      budgetTotals[rowIndex].amountYear      += calculateTotals(budgetItem.amount, budgetItem.frequency, 'YEAR');

      const yearlyDollars = typeof budgetItem.yearlyAmountCents === 'number'
        ? budgetItem.yearlyAmountCents / 100
        : calculateTotals(budgetItem.amount, budgetItem.frequency, 'YEAR');

      const objIdx = accessArray.findIndex(s => s.subtype === budgetItem.itemSubType);
      if (objIdx >= 0) accessArray[objIdx].totalAmount += yearlyDollars;
      else             accessArray.push({ subtype: budgetItem.itemSubType, totalAmount: yearlyDollars });

      allItems.push({
        name: budgetItem.itemSubType || budgetItem.description || 'Unknown',
        type: budgetItem.itemType,
        yearlyAmount: yearlyDollars,
        frequency: FREQ_LABELS[budgetItem.frequency] || budgetItem.frequency,
      });
    }

    // Leftover row
    budgetTotals.push({
      index: 4, typeTotal: 'Leftover',
      amountWeek:      budgetTotals[0].amountWeek      - budgetTotals[1].amountWeek      - budgetTotals[2].amountWeek,
      amountFortnight: budgetTotals[0].amountFortnight - budgetTotals[1].amountFortnight - budgetTotals[2].amountFortnight,
      amountMonth:     budgetTotals[0].amountMonth     - budgetTotals[1].amountMonth     - budgetTotals[2].amountMonth,
      amountQuarter:   budgetTotals[0].amountQuarter   - budgetTotals[1].amountQuarter   - budgetTotals[2].amountQuarter,
      amountYear:      budgetTotals[0].amountYear      - budgetTotals[1].amountYear      - budgetTotals[2].amountYear,
    });

    // Override with server summary if available
    if (summary && summary.aggregates) {
      const { income = 0, savings = 0, expense = 0 } = summary.aggregates;
      const leftover = summary.leftover ?? (income - savings - expense);
      const toDollars = (c: number) => (c || 0) / 100;
      const deriveBreakdowns = (year: number) => ({
        amountYear: year, amountQuarter: year / 4, amountMonth: year / 12,
        amountFortnight: year / 26, amountWeek: year / 52,
      });
      ([['Income', income], ['Savings', savings], ['Expenses', expense], ['Leftover', leftover]] as [string, number][]).forEach(([label, cents]) => {
        const idx = budgetTotals.findIndex(b => b.typeTotal === label);
        if (idx >= 0) Object.assign(budgetTotals[idx], deriveBreakdowns(toDollars(cents)));
      });
    }

    // Build subtype chart data from subtype arrays
    ([
      [incomeSubtypes, dataIncome, budgetTotals.find(b => b.typeTotal === 'Income')],
      [savingSubtypes, dataSavings, budgetTotals.find(b => b.typeTotal === 'Savings')],
      [expenseSubtypes, dataExpenses, budgetTotals.find(b => b.typeTotal === 'Expenses')],
    ] as [typeof incomeSubtypes, (string | number)[][], BudgetTotalRow | undefined][]).forEach(([subtypes, dataArr, total]) => {
      subtypes.forEach(sub => {
        sub.percentage = ((sub.totalAmount / (total?.amountYear || 1)) * 100).toFixed(2);
        dataArr.push([sub.subtype, sub.totalAmount]);
      });
    });

    // Pretty format for ag-grid
    budgetTotals.forEach(type => {
      type.amountWeekPretty      = parseFloat(String(type.amountWeek)).toLocaleString();
      type.amountFortnightPretty = parseFloat(String(type.amountFortnight)).toLocaleString();
      type.amountMonthPretty     = parseFloat(String(type.amountMonth)).toLocaleString();
      type.amountQuarterPretty   = parseFloat(String(type.amountQuarter)).toLocaleString();
      type.amountYearPretty      = parseFloat(String(type.amountYear)).toLocaleString();
    });

    // Recharts distribution data
    let rechartsIncome: ChartDatum[]   = dataIncome.slice(1).map(([name, value]) => ({ name: name as string, value: value as number }));
    let rechartsSavings: ChartDatum[]  = dataSavings.slice(1).map(([name, value]) => ({ name: name as string, value: value as number }));
    let rechartsExpenses: ChartDatum[] = dataExpenses.slice(1).map(([name, value]) => ({ name: name as string, value: value as number }));

    if (summary && summary.subtypes) {
      const toChart = (obj: Record<string, number>): ChartDatum[] => Object.entries(obj || {})
        .map(([n, v]) => ({ name: n, value: Math.round((v || 0) / 100) }))
        .sort((a, b) => b.value - a.value);
      rechartsIncome   = toChart(summary.subtypes.income);
      rechartsSavings  = toChart(summary.subtypes.savings);
      rechartsExpenses = toChart(summary.subtypes.expense);
    }

    // Allocation donut data
    let dataOverall: ChartDatum[];
    if (summary && summary.aggregates) {
      const { savings = 0, expense = 0 } = summary.aggregates;
      const incomeCents   = summary.aggregates.income || 0;
      const leftoverCents = summary.leftover != null ? summary.leftover : (incomeCents - savings - expense);
      dataOverall = [
        { name: 'Savings',  value: Math.max(0, Math.round(savings / 100)) },
        { name: 'Expenses', value: Math.max(0, Math.round(expense / 100)) },
        { name: 'Leftover', value: Math.max(0, Math.round(leftoverCents / 100)) },
      ];
    } else {
      const s = budgetTotals.find(b => b.typeTotal === 'Savings');
      const e = budgetTotals.find(b => b.typeTotal === 'Expenses');
      const l = budgetTotals.find(b => b.typeTotal === 'Leftover');
      dataOverall = [
        { name: 'Savings',  value: parseInt(String(s?.amountYear || 0)) },
        { name: 'Expenses', value: parseInt(String(e?.amountYear || 0)) },
        { name: 'Leftover', value: parseInt(String(l?.amountYear || 0)) },
      ];
    }

    // --- New widget data ---

    // Savings rate: savings / income as percentage
    const incomeYear = budgetTotals[0].amountYear;
    const savingsYear = budgetTotals[1].amountYear;
    const expensesYear = budgetTotals[2].amountYear;
    const savingsRate = incomeYear > 0 ? (savingsYear / incomeYear) * 100 : 0;

    // Top 5 items by yearly amount (across all types)
    const topItems = [...allItems].sort((a, b) => b.yearlyAmount - a.yearlyAmount).slice(0, 5);

    // Expense breakdown bar data (sorted desc)
    const expenseBarData: ExpenseBar[] = rechartsExpenses
      .map(d => ({ name: d.name, amount: d.value }))
      .sort((a, b) => b.amount - a.amount);

    // 50/30/20 rule comparison
    const expensePct = incomeYear > 0 ? (expensesYear / incomeYear) * 100 : 0;
    const savingsPct = incomeYear > 0 ? (savingsYear / incomeYear) * 100 : 0;
    const leftoverPct = Math.max(0, 100 - expensePct - savingsPct);
    const ruleComparison: RuleComparison[] = [
      { label: 'Expenses', actual: expensePct, guideline: 100 - savingsTarget, color: '#ffb347' },
      { label: 'Savings',  actual: savingsPct,  guideline: savingsTarget, color: '#6ccf7d' },
    ];
    // If there's meaningful leftover, show it as unallocated
    if (leftoverPct > 0.5) {
      ruleComparison.push({ label: 'Unallocated', actual: leftoverPct, guideline: 0, color: '#8c96d7' });
    }

    return {
      budgetTotals, rechartsIncome, rechartsSavings, rechartsExpenses, dataOverall,
      savingsRate, topItems, expenseBarData, ruleComparison,
    };
  }, [itemsData, summary, savingsTarget]);

  return {
    isAuthenticated,
    isLoading,
    summaryLoading,
    summaryError,
    ...computed,
  };
}
